import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { connect } from "react-redux";
import { Button } from 'devextreme-react/button';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { LoadPanel } from 'devextreme-react/load-panel';
import Toolbar, { Item } from "devextreme-react/toolbar";
import PrimaryTab from "./tabs/PrimaryTab";
import AdditionalTab from "./tabs/AdditionalTab";
import UserDefinedTab from "./tabs/UserDefinedTab";
import ChargesTab from "./tabs/ChargesTab";
import MinimumsTab from "./tabs/MinimumsTab";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import { readProfileDefaults } from "../../../../common/profileDefaults";
import { CONCURRENCY_HANDLED, getApiErrorMessage } from "../../../../common/concurrency";
import { MESSAGES } from "../../../../common/Messages";
import { getHeaderError, NEW_HEADER_DEFAULTS } from "./rateScheduleRules";
import {
    EMPTY_GUID,
    RECORD_TYPE,
    RateScheduleProfileRoutes,
    RateScheduleCodeRoutes,
} from "../../../../const/constants";

/**
 * A WMRATESCHEDULEHEADER read (GetRateScheduleHeader, PascalCase) as the form's ALL-CAPS fields. One
 * mapping for both the record load and the profile-template copy, so the two cannot drift apart.
 */
const mapHeaderToFormFields = (item) => ({
    ROWID: item.RowId || EMPTY_GUID,
    RECORD: item.Record || '',
    RECORDTYPE: item.RecordType || '',
    R_PROFILECODE: item.RProfileCode || null,
    R_CUSTOMER: item.RCustomer || '',
    ITEMCLASS: item.ItemClass || '',
    ITEMSUBCLASS: item.ItemSubClass || '',
    SCHEDULECODE: item.ScheduleCode || '',
    SCHEDULETYPE: item.ScheduleType || '',
    DESCRIPTION: item.Description || '',
    SCHEDULEAPPLY: item.ScheduleApply || '',
    POPUPMESSAGE: item.PopupMessage || '',
    COMMENT: item.Comment || '',
    LOTSTORAGEENDDATE: item.LotStorageEndDate || '',
    SUBLOTSTORAGEENDDATE: item.SublotStorageEndDate || '',
    RECURRINGSTORAGE: item.RecurringStorage || '',
    USERDEFINEDCODE1: item.UserDefinedCode1 || '',
    USERDEFINEDCODE2: item.UserDefinedCode2 || '',
    USERDEFINEDCODE3: item.UserDefinedCode3 || '',
    USERDEFINEDCODE4: item.UserDefinedCode4 || '',
    USERDEFINEDCODE5: item.UserDefinedCode5 || '',
    USERDEFINEDCODE6: item.UserDefinedCode6 || '',
    USERDEFINEDCODE7: item.UserDefinedCode7 || '',
    USERDEFINEDCODE8: item.UserDefinedCode8 || '',
    USERDEFINEDCODE9: item.UserDefinedCode9 || '',
    USERDEFINEDCODE10: item.UserDefinedCode10 || '',
    USERDEFINEDDATE1: item.UserDefinedDate1 || null,
    USERDEFINEDDATE2: item.UserDefinedDate2 || null,
    USERDEFINEDDATE3: item.UserDefinedDate3 || null,
    USERDEFINEDDATE4: item.UserDefinedDate4 || null,
    USERDEFINEDDATE5: item.UserDefinedDate5 || null,
    USERDEFINEDDATE6: item.UserDefinedDate6 || null,
    USERDEFINEDDATE7: item.UserDefinedDate7 || null,
    USERDEFINEDDATE8: item.UserDefinedDate8 || null,
    USERDEFINEDDATE9: item.UserDefinedDate9 || null,
    USERDEFINEDDATE10: item.UserDefinedDate10 || null,
    USERDEFINEDQUANTITY1: item.UserDefinedQuantity1 ?? null,
    USERDEFINEDQUANTITY2: item.UserDefinedQuantity2 ?? null,
    USERDEFINEDQUANTITY3: item.UserDefinedQuantity3 ?? null,
    USERDEFINEDQUANTITY4: item.UserDefinedQuantity4 ?? null,
    USERDEFINEDQUANTITY5: item.UserDefinedQuantity5 ?? null,
    USERDEFINEDQUANTITY6: item.UserDefinedQuantity6 ?? null,
    USERDEFINEDQUANTITY7: item.UserDefinedQuantity7 ?? null,
    USERDEFINEDQUANTITY8: item.UserDefinedQuantity8 ?? null,
    USERDEFINEDQUANTITY9: item.UserDefinedQuantity9 ?? null,
    USERDEFINEDQUANTITY10: item.UserDefinedQuantity10 ?? null,
    USERDEFINEDREFERENCE1: item.UserDefinedReference1 || '',
    USERDEFINEDREFERENCE2: item.UserDefinedReference2 || '',
    USERDEFINEDREFERENCE3: item.UserDefinedReference3 || '',
    USERDEFINEDREFERENCE4: item.UserDefinedReference4 || '',
    USERDEFINEDREFERENCE5: item.UserDefinedReference5 || '',
    USERDEFINEDREFERENCE6: item.UserDefinedReference6 || '',
    USERDEFINEDREFERENCE7: item.UserDefinedReference7 || '',
    USERDEFINEDREFERENCE8: item.UserDefinedReference8 || '',
    USERDEFINEDREFERENCE9: item.UserDefinedReference9 || '',
    USERDEFINEDREFERENCE10: item.UserDefinedReference10 || '',
    USERDEFINEDCOMMENT: item.UserDefinedComment || '',
    APPLY_RECEIPT: item.ApplyReceipt || '',
    APPLY_SHIPMENT: item.ApplyShipment || '',
    R_CHARGE_RECEIPT: item.RChargeReceipt || null,
    R_CHARGE_SHIPMENT: item.RChargeShipment || null,
    MINIMUMAMOUNT_RECEIPT: item.MinimumAmountReceipt ?? null,
    MINIMUMAMOUNT_SHIPMENT: item.MinimumAmountShipment ?? null,
    STATUS: item.Status || 'A',
    // Opaque optimistic-concurrency token: echoed back on SAVE, replaced from every save response.
    CONCURRENCYTOKEN: item.ConcurrencyToken || null,
});

/**
 * The form's ALL-CAPS fields as the Insert/Update payload. Shared by SAVE and by the profile template
 * created on open, so both send the same row.
 */
const toHeaderPayload = (fields) => ({
    RowId: fields.ROWID && fields.ROWID !== EMPTY_GUID ? fields.ROWID : EMPTY_GUID,
    KeySequence: 0,
    // 'P' tells the API this is a profile template: no Schedule Code, stamped 'P'.
    RecordType: fields.RECORDTYPE || null,
    RProfileCode: fields.R_PROFILECODE || null,
    RCustomer: fields.R_CUSTOMER || null,
    ItemClass: fields.ITEMCLASS || "",
    ItemSubClass: fields.ITEMSUBCLASS || "",
    ScheduleCode: fields.SCHEDULECODE || "",
    ScheduleType: fields.SCHEDULETYPE || "",
    Description: fields.DESCRIPTION || "",
    ScheduleApply: fields.SCHEDULEAPPLY || "",
    PopupMessage: fields.POPUPMESSAGE || "",
    Comment: fields.COMMENT || "",
    LotStorageEndDate: fields.LOTSTORAGEENDDATE || "",
    SublotStorageEndDate: fields.SUBLOTSTORAGEENDDATE || "",
    RecurringStorage: fields.RECURRINGSTORAGE || "",
    UserDefinedCode1: fields.USERDEFINEDCODE1 || "",
    UserDefinedCode2: fields.USERDEFINEDCODE2 || "",
    UserDefinedCode3: fields.USERDEFINEDCODE3 || "",
    UserDefinedCode4: fields.USERDEFINEDCODE4 || "",
    UserDefinedCode5: fields.USERDEFINEDCODE5 || "",
    UserDefinedCode6: fields.USERDEFINEDCODE6 || "",
    UserDefinedCode7: fields.USERDEFINEDCODE7 || "",
    UserDefinedCode8: fields.USERDEFINEDCODE8 || "",
    UserDefinedCode9: fields.USERDEFINEDCODE9 || "",
    UserDefinedCode10: fields.USERDEFINEDCODE10 || "",
    UserDefinedDate1: fields.USERDEFINEDDATE1 || null,
    UserDefinedDate2: fields.USERDEFINEDDATE2 || null,
    UserDefinedDate3: fields.USERDEFINEDDATE3 || null,
    UserDefinedDate4: fields.USERDEFINEDDATE4 || null,
    UserDefinedDate5: fields.USERDEFINEDDATE5 || null,
    UserDefinedDate6: fields.USERDEFINEDDATE6 || null,
    UserDefinedDate7: fields.USERDEFINEDDATE7 || null,
    UserDefinedDate8: fields.USERDEFINEDDATE8 || null,
    UserDefinedDate9: fields.USERDEFINEDDATE9 || null,
    UserDefinedDate10: fields.USERDEFINEDDATE10 || null,
    UserDefinedQuantity1: fields.USERDEFINEDQUANTITY1 ?? null,
    UserDefinedQuantity2: fields.USERDEFINEDQUANTITY2 ?? null,
    UserDefinedQuantity3: fields.USERDEFINEDQUANTITY3 ?? null,
    UserDefinedQuantity4: fields.USERDEFINEDQUANTITY4 ?? null,
    UserDefinedQuantity5: fields.USERDEFINEDQUANTITY5 ?? null,
    UserDefinedQuantity6: fields.USERDEFINEDQUANTITY6 ?? null,
    UserDefinedQuantity7: fields.USERDEFINEDQUANTITY7 ?? null,
    UserDefinedQuantity8: fields.USERDEFINEDQUANTITY8 ?? null,
    UserDefinedQuantity9: fields.USERDEFINEDQUANTITY9 ?? null,
    UserDefinedQuantity10: fields.USERDEFINEDQUANTITY10 ?? null,
    UserDefinedReference1: fields.USERDEFINEDREFERENCE1 || "",
    UserDefinedReference2: fields.USERDEFINEDREFERENCE2 || "",
    UserDefinedReference3: fields.USERDEFINEDREFERENCE3 || "",
    UserDefinedReference4: fields.USERDEFINEDREFERENCE4 || "",
    UserDefinedReference5: fields.USERDEFINEDREFERENCE5 || "",
    UserDefinedReference6: fields.USERDEFINEDREFERENCE6 || "",
    UserDefinedReference7: fields.USERDEFINEDREFERENCE7 || "",
    UserDefinedReference8: fields.USERDEFINEDREFERENCE8 || "",
    UserDefinedReference9: fields.USERDEFINEDREFERENCE9 || "",
    UserDefinedReference10: fields.USERDEFINEDREFERENCE10 || "",
    UserDefinedComment: fields.USERDEFINEDCOMMENT || "",
    ApplyReceipt: fields.APPLY_RECEIPT || "",
    ApplyShipment: fields.APPLY_SHIPMENT || "",
    RChargeReceipt: fields.R_CHARGE_RECEIPT || null,
    RChargeShipment: fields.R_CHARGE_SHIPMENT || null,
    MinimumAmountReceipt: fields.MINIMUMAMOUNT_RECEIPT,
    MinimumAmountShipment: fields.MINIMUMAMOUNT_SHIPMENT,
    Status: fields.STATUS || "A",
    ConcurrencyToken: fields.CONCURRENCYTOKEN || null,
});

function Form(props) {
    const history = useHistory();
    const formRefs = useRef({});

    // Latest formFields, for the callbacks below that are captured on an earlier render.
    const formFieldsRef = useRef(null);

    // The Rate Schedule Profile screen's "Rate Schedule Defaults" button opens this form on the
    // profile's template header (uWMD0017.SetParameters): ?recordType=P&RProfile=<profile>. A record
    // opened for edit carries its record type in state, so that counts as well as the URL.
    const readDefaults = () => readProfileDefaults(window.location.search, formFieldsRef.current?.RECORDTYPE);
    const { isProfileDefaults } = readDefaults();
    const hasOpenedProfileTemplate = useRef(false);

    const initialFormFields = {
        ROWID: '00000000-0000-0000-0000-000000000000',
        RECORD: '',
        // 'P' for a profile template, '' otherwise (the API stamps 'R').
        RECORDTYPE: isProfileDefaults ? RECORD_TYPE.PROFILE_DEFAULTS : '',
        R_PROFILECODE: null,
        R_CUSTOMER: '',
        ITEMCLASS: '',
        ITEMSUBCLASS: '',
        SCHEDULECODE: '',
        SCHEDULETYPE: NEW_HEADER_DEFAULTS.SCHEDULETYPE,
        DESCRIPTION: '',
        SCHEDULEAPPLY: NEW_HEADER_DEFAULTS.SCHEDULEAPPLY,
        POPUPMESSAGE: '',
        COMMENT: '',
        LOTSTORAGEENDDATE: NEW_HEADER_DEFAULTS.LOTSTORAGEENDDATE,
        SUBLOTSTORAGEENDDATE: NEW_HEADER_DEFAULTS.SUBLOTSTORAGEENDDATE,
        RECURRINGSTORAGE: NEW_HEADER_DEFAULTS.RECURRINGSTORAGE,
        USERDEFINEDCODE1: '',
        USERDEFINEDCODE2: '',
        USERDEFINEDCODE3: '',
        USERDEFINEDCODE4: '',
        USERDEFINEDCODE5: '',
        USERDEFINEDCODE6: '',
        USERDEFINEDCODE7: '',
        USERDEFINEDCODE8: '',
        USERDEFINEDCODE9: '',
        USERDEFINEDCODE10: '',
        USERDEFINEDDATE1: null,
        USERDEFINEDDATE2: null,
        USERDEFINEDDATE3: null,
        USERDEFINEDDATE4: null,
        USERDEFINEDDATE5: null,
        USERDEFINEDDATE6: null,
        USERDEFINEDDATE7: null,
        USERDEFINEDDATE8: null,
        USERDEFINEDDATE9: null,
        USERDEFINEDDATE10: null,
        USERDEFINEDQUANTITY1: null,
        USERDEFINEDQUANTITY2: null,
        USERDEFINEDQUANTITY3: null,
        USERDEFINEDQUANTITY4: null,
        USERDEFINEDQUANTITY5: null,
        USERDEFINEDQUANTITY6: null,
        USERDEFINEDQUANTITY7: null,
        USERDEFINEDQUANTITY8: null,
        USERDEFINEDQUANTITY9: null,
        USERDEFINEDQUANTITY10: null,
        USERDEFINEDREFERENCE1: '',
        USERDEFINEDREFERENCE2: '',
        USERDEFINEDREFERENCE3: '',
        USERDEFINEDREFERENCE4: '',
        USERDEFINEDREFERENCE5: '',
        USERDEFINEDREFERENCE6: '',
        USERDEFINEDREFERENCE7: '',
        USERDEFINEDREFERENCE8: '',
        USERDEFINEDREFERENCE9: '',
        USERDEFINEDREFERENCE10: '',
        USERDEFINEDCOMMENT: '',
        APPLY_RECEIPT: '',
        APPLY_SHIPMENT: '',
        R_CHARGE_RECEIPT: null,
        R_CHARGE_SHIPMENT: null,
        MINIMUMAMOUNT_RECEIPT: null,
        MINIMUMAMOUNT_SHIPMENT: null,
        STATUS: NEW_HEADER_DEFAULTS.STATUS,
        CONCURRENCYTOKEN: null,
    };
    const [formFields, setformFields] = useState(initialFormFields);
    // Shown from the first paint whenever a record is about to load - edit mode, or the Rate Schedule
    // Defaults route, which opens (or seeds) the profile's template - so the empty form never flashes first.
    const [loadPanelVisible, setLoadPanelVisible] = useState(() => {
        const { isProfileDefaults: isProfileDefaultsRoute, profileRowId } = readDefaults();
        return !!(props.id && props.id !== EMPTY_GUID) || !!(isProfileDefaultsRoute && profileRowId);
    });

    // Synced during render, not in an effect: the form's lookups report value changes from their own
    // effects, which run before this component's effects - an effect-synced ref would still hold the
    // previous record there, and a freshly loaded record would look like an unsaved one.
    formFieldsRef.current = formFields;

    // Latest profile-change handler, so the memoised handleFieldChange never calls a stale closure.
    const profileChangedRef = useRef(null);

    // Delphi FO_Profile: the profile this record last accepted, which a change is compared with. Kept
    // apart from formFields on purpose - FormField writes the new value into formFields before it calls
    // handleFieldChange, and DevExtreme events are outside React's batching, so by then formFields
    // (and formFieldsRef) already hold the NEW profile and cannot tell what it changed from.
    const acceptedProfileRef = useRef(null);

    // The Charges tab's grids, so Save can commit the rows still being edited there.
    const dateGrid = useRef(null);
    const detailGrid = useRef(null);
    const accessToken = props?.oidc?.user?.access_token;

    // The bearer headers every API call in this form sends.
    const authHeaders = useCallback(() => ({
        access_token: accessToken,
        Authorization: `Bearer ${accessToken}`
    }), [accessToken]);

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const position = { of: '#accordion' };

    const handleFieldChange = useCallback((name, value) => {
        setformFields(prevFields => ({
            ...prevFields,
            [name]: value
        }));

        if (name === 'R_PROFILECODE' && profileChangedRef.current) {
            profileChangedRef.current(value);
        }
    }, []);

    const loadRateScheduleHeaderById = useCallback(async (rowId) => {
        if (!rowId || rowId === '00000000-0000-0000-0000-000000000000') {
            return;
        }

        try {
            setLoadPanelVisible(true);
            const url = `${window._env_.REACT_APP_APIS}/${data.RateScheduleCode.GetRateScheduleHeader}?rowid=${rowId}`;
            const response = await axios.get(url, { headers: authHeaders() });

            if (response.data) {
                setformFields(mapHeaderToFormFields(response.data));
                acceptedProfileRef.current = response.data.RProfileCode || null;
            }
        } catch (error) {
            console.error('Error loading Rate Schedule Header:', error);
            notify(MESSAGES.RATE_SCHEDULE_HEADER_LOAD_ERROR, 'error', 3000);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [authHeaders]);

    useEffect(() => {
        const rowId = props.id;
        if (rowId && rowId !== '00000000-0000-0000-0000-000000000000') {
            loadRateScheduleHeaderById(rowId);
        }
    }, [props.id, loadRateScheduleHeaderById]);

    /**
     * RowId of the profile's RECORDTYPE='P' template header, or null when it has none. The profile read
     * carries it as RateScheduleDefaultsRowId (the same template RateScheduleHeaderService copies from).
     */
    const fetchProfileTemplateRowId = async (profileRowId) => {
        const profile = await axios.get(
            `${window._env_.REACT_APP_APIS}/${data.RateScheduleDp.GetRateScheduleDp}?rowid=${profileRowId}`,
            { headers: authHeaders() });
        return profile?.data?.RateScheduleDefaultsRowId || null;
    };

    /**
     * uWMD0017.edtP_PROFILEExit: copies the profile's template header onto the new rate schedule -
     * every field except ROWID, with the audit block cleared and RECORDTYPE 'R'. The template is the
     * profile's RECORDTYPE='P' header, whose RowId the profile read carries as
     * RateScheduleDefaultsRowId. When the new schedule is saved, RateScheduleHeaderService copies the
     * template's dates and charges too (Delphi qryWMRateScheduleHeaderApplyUpdates).
     */
    const applyProfileTemplate = async (profileRowId) => {
        try {
            setLoadPanelVisible(true);

            const templateRowId = await fetchProfileTemplateRowId(profileRowId);
            if (!templateRowId) {
                return; // Delphi: "if RecordsExist" - a profile with no template changes nothing.
            }

            const template = await axios.get(
                `${window._env_.REACT_APP_APIS}/${data.RateScheduleCode.GetRateScheduleHeader}?rowid=${templateRowId}`,
                { headers: authHeaders() });
            if (!template?.data) {
                return;
            }

            setformFields(prev => ({
                ...mapHeaderToFormFields(template.data),
                // Identity and audit are the new record's own, never the template's.
                ROWID: prev.ROWID,
                RECORD: prev.RECORD,
                RECORDTYPE: prev.RECORDTYPE,
                CONCURRENCYTOKEN: prev.CONCURRENCYTOKEN,
                R_PROFILECODE: profileRowId,
            }));
        } catch (error) {
            console.error('Error loading the rate schedule profile defaults:', error);
            notify(getApiErrorMessage(error, MESSAGES.FETCH_ERROR), 'error', 3000);
        } finally {
            setLoadPanelVisible(false);
        }
    };

    /**
     * Delphi exits when the record is not being added, is a profile template, or has no profile; asks
     * "You Have Changed Your Profile Code. Continue?" when a profile was already chosen; and on "No"
     * puts the previous profile back.
     */
    const handleProfileChanged = (newProfile) => {
        const current = formFieldsRef.current;
        const isNewRecord = !current?.ROWID || current.ROWID === EMPTY_GUID;
        const previousProfile = acceptedProfileRef.current;

        if (!isNewRecord || readDefaults().isProfileDefaults || newProfile === previousProfile) {
            return;
        }

        // Cleared: nothing to copy, and the next pick is a first pick again (Delphi re-reads
        // FO_Profile on entering the field, which is then blank).
        if (!newProfile) {
            acceptedProfileRef.current = null;
            return;
        }

        if (!previousProfile) {
            acceptedProfileRef.current = newProfile;
            applyProfileTemplate(newProfile);
            return;
        }

        confirm(MESSAGES.PROFILE_CODE_CHANGED_CONFIRM, MESSAGES.CONFIRM).then((dialogResult) => {
            if (dialogResult) {
                acceptedProfileRef.current = newProfile;
                applyProfileTemplate(newProfile);
            } else {
                // Restoring it re-enters handleFieldChange with previousProfile, which equals the
                // accepted profile and so does nothing.
                setformFields(prev => ({ ...prev, R_PROFILECODE: previousProfile }));
            }
        });
    };
    profileChangedRef.current = handleProfileChanged;

    /**
     * Commits the rows still being edited in the Charges tab's grids. A row the grid refuses - a date
     * with no Effective Date, a charge line with no Charge Code - stays pending, and the grid has already
     * said why. Returns false then, so Save stops instead of reporting success while the row is lost.
     */
    const commitChargesGridEdits = async () => {
        for (const gridRef of [dateGrid, detailGrid]) {
            const grid = gridRef.current?.instance?.();
            if (grid?.hasEditData()) {
                await grid.saveEditData();
                if (grid.hasEditData()) {
                    return false;
                }
            }
        }

        return true;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        // Delphi qryWMRateScheduleHeaderBeforePost; the service enforces the same rule.
        const headerError = getHeaderError(formFields);
        if (headerError) {
            notify(headerError, "error", 5000);
            return;
        }

        if (!(await commitChargesGridEdits())) {
            return;
        }

        setLoadPanelVisible(true);

        try {
            const payload = toHeaderPayload(formFields);

            const isUpdate = formFields.ROWID && formFields.ROWID !== "00000000-0000-0000-0000-000000000000";
            const endpoint = isUpdate ? data.RateScheduleCode.UpdateRateScheduleHeader : data.RateScheduleCode.InsertRateScheduleHeader;
            const method = isUpdate ? 'put' : 'post';

            const url = `${window._env_.REACT_APP_APIS}/${endpoint}`;
            const resp = await axios[method](url, payload, { headers: authHeaders() });

            const saved = resp?.data;
            if (saved) {
                setformFields((prev) => ({
                    ...prev,
                    ROWID: saved.RowId || prev.ROWID,
                    RECORD: saved.Record || prev.RECORD,
                    RECORDTYPE: saved.RecordType || prev.RECORDTYPE,
                    R_PROFILECODE: saved.RProfileCode || prev.R_PROFILECODE,
                    R_CUSTOMER: saved.RCustomer || prev.R_CUSTOMER,
                    ITEMCLASS: saved.ItemClass || prev.ITEMCLASS,
                    ITEMSUBCLASS: saved.ItemSubClass || prev.ITEMSUBCLASS,
                    SCHEDULECODE: saved.ScheduleCode || prev.SCHEDULECODE,
                    SCHEDULETYPE: saved.ScheduleType || prev.SCHEDULETYPE,
                    DESCRIPTION: saved.Description || prev.DESCRIPTION,
                    SCHEDULEAPPLY: saved.ScheduleApply || prev.SCHEDULEAPPLY,
                    POPUPMESSAGE: saved.PopupMessage || prev.POPUPMESSAGE,
                    COMMENT: saved.Comment || prev.COMMENT,
                    LOTSTORAGEENDDATE: saved.LotStorageEndDate || prev.LOTSTORAGEENDDATE,
                    SUBLOTSTORAGEENDDATE: saved.SublotStorageEndDate || prev.SUBLOTSTORAGEENDDATE,
                    RECURRINGSTORAGE: saved.RecurringStorage || prev.RECURRINGSTORAGE,
                    USERDEFINEDCODE1: saved.UserDefinedCode1 || prev.USERDEFINEDCODE1,
                    USERDEFINEDCODE2: saved.UserDefinedCode2 || prev.USERDEFINEDCODE2,
                    USERDEFINEDCODE3: saved.UserDefinedCode3 || prev.USERDEFINEDCODE3,
                    USERDEFINEDCODE4: saved.UserDefinedCode4 || prev.USERDEFINEDCODE4,
                    USERDEFINEDCODE5: saved.UserDefinedCode5 || prev.USERDEFINEDCODE5,
                    USERDEFINEDCODE6: saved.UserDefinedCode6 || prev.USERDEFINEDCODE6,
                    USERDEFINEDCODE7: saved.UserDefinedCode7 || prev.USERDEFINEDCODE7,
                    USERDEFINEDCODE8: saved.UserDefinedCode8 || prev.USERDEFINEDCODE8,
                    USERDEFINEDCODE9: saved.UserDefinedCode9 || prev.USERDEFINEDCODE9,
                    USERDEFINEDCODE10: saved.UserDefinedCode10 || prev.USERDEFINEDCODE10,
                    USERDEFINEDDATE1: saved.UserDefinedDate1 || prev.USERDEFINEDDATE1,
                    USERDEFINEDDATE2: saved.UserDefinedDate2 || prev.USERDEFINEDDATE2,
                    USERDEFINEDDATE3: saved.UserDefinedDate3 || prev.USERDEFINEDDATE3,
                    USERDEFINEDDATE4: saved.UserDefinedDate4 || prev.USERDEFINEDDATE4,
                    USERDEFINEDDATE5: saved.UserDefinedDate5 || prev.USERDEFINEDDATE5,
                    USERDEFINEDDATE6: saved.UserDefinedDate6 || prev.USERDEFINEDDATE6,
                    USERDEFINEDDATE7: saved.UserDefinedDate7 || prev.USERDEFINEDDATE7,
                    USERDEFINEDDATE8: saved.UserDefinedDate8 || prev.USERDEFINEDDATE8,
                    USERDEFINEDDATE9: saved.UserDefinedDate9 || prev.USERDEFINEDDATE9,
                    USERDEFINEDDATE10: saved.UserDefinedDate10 || prev.USERDEFINEDDATE10,
                    USERDEFINEDQUANTITY1: saved.UserDefinedQuantity1 ?? prev.USERDEFINEDQUANTITY1,
                    USERDEFINEDQUANTITY2: saved.UserDefinedQuantity2 ?? prev.USERDEFINEDQUANTITY2,
                    USERDEFINEDQUANTITY3: saved.UserDefinedQuantity3 ?? prev.USERDEFINEDQUANTITY3,
                    USERDEFINEDQUANTITY4: saved.UserDefinedQuantity4 ?? prev.USERDEFINEDQUANTITY4,
                    USERDEFINEDQUANTITY5: saved.UserDefinedQuantity5 ?? prev.USERDEFINEDQUANTITY5,
                    USERDEFINEDQUANTITY6: saved.UserDefinedQuantity6 ?? prev.USERDEFINEDQUANTITY6,
                    USERDEFINEDQUANTITY7: saved.UserDefinedQuantity7 ?? prev.USERDEFINEDQUANTITY7,
                    USERDEFINEDQUANTITY8: saved.UserDefinedQuantity8 ?? prev.USERDEFINEDQUANTITY8,
                    USERDEFINEDQUANTITY9: saved.UserDefinedQuantity9 ?? prev.USERDEFINEDQUANTITY9,
                    USERDEFINEDQUANTITY10: saved.UserDefinedQuantity10 ?? prev.USERDEFINEDQUANTITY10,
                    USERDEFINEDREFERENCE1: saved.UserDefinedReference1 || prev.USERDEFINEDREFERENCE1,
                    USERDEFINEDREFERENCE2: saved.UserDefinedReference2 || prev.USERDEFINEDREFERENCE2,
                    USERDEFINEDREFERENCE3: saved.UserDefinedReference3 || prev.USERDEFINEDREFERENCE3,
                    USERDEFINEDREFERENCE4: saved.UserDefinedReference4 || prev.USERDEFINEDREFERENCE4,
                    USERDEFINEDREFERENCE5: saved.UserDefinedReference5 || prev.USERDEFINEDREFERENCE5,
                    USERDEFINEDREFERENCE6: saved.UserDefinedReference6 || prev.USERDEFINEDREFERENCE6,
                    USERDEFINEDREFERENCE7: saved.UserDefinedReference7 || prev.USERDEFINEDREFERENCE7,
                    USERDEFINEDREFERENCE8: saved.UserDefinedReference8 || prev.USERDEFINEDREFERENCE8,
                    USERDEFINEDREFERENCE9: saved.UserDefinedReference9 || prev.USERDEFINEDREFERENCE9,
                    USERDEFINEDREFERENCE10: saved.UserDefinedReference10 || prev.USERDEFINEDREFERENCE10,
                    USERDEFINEDCOMMENT: saved.UserDefinedComment || prev.USERDEFINEDCOMMENT,
                    APPLY_RECEIPT: saved.ApplyReceipt || prev.APPLY_RECEIPT,
                    APPLY_SHIPMENT: saved.ApplyShipment || prev.APPLY_SHIPMENT,
                    R_CHARGE_RECEIPT: saved.RChargeReceipt || prev.R_CHARGE_RECEIPT,
                    R_CHARGE_SHIPMENT: saved.RChargeShipment || prev.R_CHARGE_SHIPMENT,
                    MINIMUMAMOUNT_RECEIPT: saved.MinimumAmountReceipt ?? prev.MINIMUMAMOUNT_RECEIPT,
                    MINIMUMAMOUNT_SHIPMENT: saved.MinimumAmountShipment ?? prev.MINIMUMAMOUNT_SHIPMENT,
                    STATUS: saved.Status || prev.STATUS,
                    // Without the refreshed token the next SAVE would falsely conflict.
                    CONCURRENCYTOKEN: saved.ConcurrencyToken || prev.CONCURRENCYTOKEN,
                }));
            }

            notify(MESSAGES.RATE_SCHEDULE_HEADER_SAVE_SUCCESS, "success", 3000);
        } catch (err) {
            // A stale save is already handled by the axios interceptor in AppContainer, which raises the
            // "record changed - reload" dialog. Don't stack a red toast on top of it.
            if (err?.[CONCURRENCY_HANDLED]) {
                return;
            }

            notify(getApiErrorMessage(err, MESSAGES.RATE_SCHEDULE_HEADER_SAVE_ERROR), "error", 5000);
        } finally {
            hideLoadPanel();
        }
    };

    /**
     * An unsaved profile template on screen: RecordType 'P' and P_ProfileCode = the profile. Used by
     * Reset, and as the fallback when the template could not be created on open, so SAVE can still
     * create it. Functional update, so it also applies on top of a reset in the same batch.
     */
    const SeedProfileDefaultsRateSchedule = (profileRowId) => {
        setformFields(prev => ({
            ...prev,
            ROWID: EMPTY_GUID,
            RECORDTYPE: RECORD_TYPE.PROFILE_DEFAULTS,
            R_PROFILECODE: profileRowId,
        }));
    };

    /**
     * The seed branch of uWMD0017.SetParameters - "if not RecordsExist then Insert; P_ProfileCode :=
     * profile; Post". Delphi posts the template there and then, so it has its Record # as soon as the
     * screen opens and qeWMRateScheduleHeaderAfterScroll re-enables the Charges tab for it. Same row a
     * SAVE of the seeded form would send. Returns the new template's RowId.
     */
    const createProfileTemplate = async (profileRowId) => {
        const template = {
            ...initialFormFields,
            RECORDTYPE: RECORD_TYPE.PROFILE_DEFAULTS,
            R_PROFILECODE: profileRowId,
        };
        const resp = await axios.post(
            `${window._env_.REACT_APP_APIS}/${data.RateScheduleCode.InsertRateScheduleHeader}`,
            toHeaderPayload(template),
            { headers: authHeaders() });
        return resp?.data?.RowId || null;
    };

    /**
     * uWMD0017.SetParameters as a whole: open the profile's template if it has one, otherwise create it.
     * The Create route is where the profile screen sends a profile with no template; checking first means
     * a refresh of that route reopens the template instead of creating a second one. (The page never
     * switches route under a live form to get there: changing props.id on this instance flips the
     * TabPanel's deferRendering, and the hidden tabs' lookup effects then hit disposed widgets.)
     *
     * The API refuses a second template for a profile, so a failed insert is re-checked: another tab may
     * have created it first. Only when there is still none does the unsaved seeded form stay on screen.
     *
     * The loading indicator stays up through the template load (which hides it itself), so it does not
     * flicker off and on between the calls.
     */
    const openProfileTemplate = async (profileRowId) => {
        setLoadPanelVisible(true);

        let templateRowId = null;
        try {
            templateRowId = await fetchProfileTemplateRowId(profileRowId)
                || await createProfileTemplate(profileRowId);
        } catch (error) {
            templateRowId = await fetchProfileTemplateRowId(profileRowId).catch(() => null);
            if (!templateRowId) {
                console.error('Error opening the rate schedule profile template:', error);
                notify(getApiErrorMessage(error, MESSAGES.RATE_SCHEDULE_DEFAULTS_CREATE_ERROR), 'error', 5000);
            }
        }

        if (templateRowId) {
            await loadRateScheduleHeaderById(templateRowId);
            return;
        }

        SeedProfileDefaultsRateSchedule(profileRowId);
        setLoadPanelVisible(false);
    };

    // The Add route reached from the profile screen's Rate Schedule Defaults button.
    useEffect(() => {
        const { isProfileDefaults: isProfileDefaultsRoute, profileRowId } = readDefaults();
        if (!props.id && isProfileDefaultsRoute && profileRowId && !hasOpenedProfileTemplate.current) {
            hasOpenedProfileTemplate.current = true;
            openProfileTemplate(profileRowId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.id]);

    const addNewRecordClick = () => {
        acceptedProfileRef.current = null;
        setformFields(initialFormFields);
    };

    const handleReset = () => {
        // On the Add route props.id stays empty after the first save, so fall back to the saved row.
        const savedRowId = formFields.ROWID && formFields.ROWID !== EMPTY_GUID ? formFields.ROWID : null;
        const rowId = props.id || savedRowId;
        if (rowId && rowId !== EMPTY_GUID) {
            loadRateScheduleHeaderById(rowId);
        } else {
            acceptedProfileRef.current = null;
            setformFields(initialFormFields);

            const { isProfileDefaults: isProfileDefaultsRoute, profileRowId } = readDefaults();
            if (isProfileDefaultsRoute && profileRowId) {
                SeedProfileDefaultsRateSchedule(profileRowId);
            }
        }
    };

    const goBack = () => {
        // A profile template returns to its profile (Delphi closes back to uWMD0005), not the listing.
        if (isProfileDefaults) {
            const { profileRowId } = readDefaults();
            const profileId = profileRowId || formFields.R_PROFILECODE;
            if (profileId) {
                history.push(`${RateScheduleProfileRoutes.Edit}?rowid=${profileId}`);
                return;
            }
        }

        history.push(RateScheduleCodeRoutes.List);
    };

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={position}
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={(e) => handleFormSubmit(e)}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6
                                            style={{
                                                color: window._env_.Color2,
                                                marginLeft: '12px'
                                            }}
                                        >
                                            Rate Schedule Code
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button
                                        text="Save"
                                        useSubmitBehavior={true}
                                        type="default"
                                        className="accordianButton"
                                    />
                                </Item>
                                {/* Delphi strips abAdd from the navigator for a profile template. */}
                                {!isProfileDefaults && (
                                    <Item location="after">
                                        <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                    </Item>
                                )}
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>
                            <TabPanel deferRendering={props.id ? true : false} showNavButtons={true} height={"auto"}>
                                <TabItem title="Primary">
                                    <PrimaryTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                        isProfileDefaults={isProfileDefaults}
                                    />
                                </TabItem>
                                <TabItem title="Additional">
                                    <AdditionalTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="User Defined">
                                    <UserDefinedTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                              
                                <TabItem title="Charges">
                                    <ChargesTab
                                        dateGrid={dateGrid}
                                        detailGrid={detailGrid}
                                        headerId={formFields.ROWID}
                                        recordType={formFields.RECORDTYPE}
                                        scheduleType={formFields.SCHEDULETYPE}
                                    />
                                </TabItem>
                                <TabItem title="Minimums">
                                    <MinimumsTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(Form);

